import { Building2, Users, Package, ShieldCheck, ArrowUpRight, ArrowRight, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api, DashboardStats } from '@/lib/api';

const statusColors: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-600',
  inactive: 'bg-gray-100 text-gray-600',
  suspended: 'bg-red-100 text-red-600',
};

const statusLabels: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  suspended: 'Suspenso',
};

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      setLoading(true);
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Visão geral da plataforma NOZ</p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm animate-pulse">
              <div className="h-12 bg-gray-200 rounded-xl w-12"></div>
              <div className="mt-4 h-8 bg-gray-200 rounded w-24"></div>
              <div className="mt-2 h-4 bg-gray-200 rounded w-32"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Visão geral da plataforma NOZ</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          {error || 'Erro ao carregar dados'}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total de Clientes',
      value: stats.tenants.total.toString(),
      subtitle: `${stats.tenants.active} ativos`,
      icon: Building2,
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-500',
    },
    {
      title: 'Planos Ativos',
      value: stats.plans.active.toString(),
      subtitle: `${stats.plans.total} no total`,
      icon: Package,
      iconBg: 'bg-violet-500/10',
      iconColor: 'text-violet-500',
    },
    {
      title: 'Features Ativas',
      value: stats.features.active.toString(),
      subtitle: `${stats.features.total} no total`,
      icon: TrendingUp,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-500',
    },
    {
      title: 'Administradores',
      value: stats.admins.active.toString(),
      subtitle: `${stats.admins.total} no total`,
      icon: ShieldCheck,
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-500',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Visão geral da plataforma NOZ</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div className={`p-3 rounded-xl ${stat.iconBg}`}>
                <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-500 mt-1">{stat.title}</p>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400">{stat.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tenant Status Distribution */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Distribuição de Clientes</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
            <p className="text-2xl font-bold text-emerald-600">{stats.tenants.active}</p>
            <p className="text-sm text-emerald-600 mt-1">Ativos</p>
          </div>
          <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-2xl font-bold text-gray-600">{stats.tenants.inactive}</p>
            <p className="text-sm text-gray-600 mt-1">Inativos</p>
          </div>
          <div className="p-4 rounded-xl bg-red-50 border border-red-100">
            <p className="text-2xl font-bold text-red-600">{stats.tenants.suspended}</p>
            <p className="text-sm text-red-600 mt-1">Suspensos</p>
          </div>
        </div>
      </div>

      {/* Tenants by Plan */}
      {stats.tenantsByPlan.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Clientes por Plano</h2>
          <div className="space-y-3">
            {stats.tenantsByPlan.map((item) => (
              <div key={item.planId} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                  <span className="font-medium text-gray-900">{item.planName}</span>
                </div>
                <span className="text-lg font-bold text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Tenants */}
      {stats.recentTenants.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Clientes Recentes</h2>
                <p className="text-sm text-gray-500 mt-1">Últimos clientes cadastrados na plataforma</p>
              </div>
              <Link
                to="/clients"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#0057FF] hover:text-[#0057FF]/80 transition-colors"
              >
                Ver todos
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.recentTenants.map((tenant) => (
              <Link
                key={tenant.id}
                to={`/tenants/${tenant.slug}`}
                className="flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0057FF] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-[#0057FF]/20">
                    <span className="text-white font-bold text-lg">
                      {tenant.platformName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{tenant.platformName}</p>
                    <p className="text-sm text-gray-500">{tenant.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-600">
                      {tenant.plan?.name || 'Sem plano'}
                    </span>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(tenant.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`h-2.5 w-2.5 rounded-full ${
                      tenant.status === 'active' ? 'bg-emerald-500' :
                      tenant.status === 'suspended' ? 'bg-red-500' : 'bg-gray-300'
                    }`} />
                    <span className="text-sm text-gray-600">
                      {statusLabels[tenant.status] || tenant.status}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Tenant Growth */}
      {stats.tenantGrowth.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Crescimento (Últimos 6 Meses)</h2>
          <div className="space-y-3">
            {stats.tenantGrowth.map((item) => (
              <div key={item.month} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{item.month}</span>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden w-48">
                    <div
                      className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full"
                      style={{
                        width: `${Math.min(100, (item.count / Math.max(...stats.tenantGrowth.map(g => g.count))) * 100)}%`
                      }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">{item.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
